export function DocsAuthenticationPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Platform</p>
      <h1 className="docs-page-title">Authentication</h1>
      <p className="docs-lede">
        Managed Woven uses two separate authentication layers: Firebase identity for the Host
        control plane and an opaque, product-scoped Bearer credential for realtime client
        connections.
      </p>

      <h2 className="docs-heading">Host control plane</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Identity</dt>
          <dd>
            Firebase Authentication signs users in with Google, GitHub, or email. The control API
            verifies the Firebase ID token and resolves the account entitlement on protected
            requests.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Recent authentication</dt>
          <dd>
            Revealing connection credentials and deleting an account require a Firebase sign-in
            completed within the previous five minutes. The web app reauthenticates the same account
            before the user explicitly retries the operation.
          </dd>
        </div>
        <div className="detail-row">
          <dt>App Check</dt>
          <dd>
            The current hosted deployment runs App Check in monitor mode: a supplied token is
            verified, while a missing token is observed rather than rejected. Required mode enforces
            a valid token on every request; emulator use disables App Check.
          </dd>
        </div>
      </dl>

      <h2 className="docs-heading">Managed runtime</h2>
      <p className="docs-body">
        When a managed product is provisioned, Host can reveal its native QUIC and browser
        WebTransport endpoints, namespace, session, and an opaque <code>Bearer</code> credential.
        This runtime-admission credential is scoped to that product generation; Firebase claims and
        individual product-user identity remain in the Host control plane. For the current browser
        path, it is one shared possession credential intended for trusted or internal clients—not an
        end-user identity token.
      </p>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Host storage</dt>
          <dd>
            Host stores the credential encrypted with AES-GCM in its private credential record,
            bound to the product generation. Credential reveal remains owner-authorized and
            recent-auth protected.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Woven verification</dt>
          <dd>
            The managed Woven node stores a verifier for the opaque credential and grants access
            only to the provisioned namespace and session. The user&apos;s Firebase token remains in
            the Host control plane.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Compromise response</dt>
          <dd>
            Any holder of the shared product-generation token can use its managed scope. Credential
            rotation and short-lived client grants are not implemented yet, so suspected compromise
            requires deleting and recreating the product to issue a new scope and credential.
          </dd>
        </div>
      </dl>

      <h2 className="docs-heading">Availability</h2>
      <p className="docs-body">
        Control-plane identity and runtime authentication are active for managed products across
        native QUIC and browser WebTransport. The TypeScript browser client is Online. Pro and
        Dedicated remain Preview.
      </p>
    </div>
  );
}
